import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export function QuorumCheckerTool() {
  const [totalMembers, setTotalMembers] = useState('100');
  const [quorumPct, setQuorumPct] = useState('50');
  const [presentMembers, setPresentMembers] = useState('45');
  const [proxies, setProxies] = useState('10');
  const [nonVoting, setNonVoting] = useState('0');

  const total = parseInt(totalMembers) || 0;
  const qPct = parseFloat(quorumPct) || 0;
  const present = parseInt(presentMembers) || 0;
  const p = parseInt(proxies) || 0;
  const nv = parseInt(nonVoting) || 0;

  const activeVotingTotal = Math.max(0, total - nv);
  const requiredToQuorum = Math.ceil((activeVotingTotal * qPct) / 100);
  
  const totalRepresented = present + p;
  const isQuorumMet = totalRepresented >= requiredToQuorum;

  const majorityThreshold = Math.ceil((totalRepresented / 2) + 0.1);
  const twoThirdsThreshold = Math.ceil((totalRepresented * 2) / 3);

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Membership Base</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">Total Roster Members</label>
                <Input type="number" value={totalMembers} onChange={e => setTotalMembers(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">Non-Voting/Suspended</label>
                <Input type="number" value={nonVoting} onChange={e => setNonVoting(e.target.value)} />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">Quorum Requirement (%)</label>
              <Input type="number" value={quorumPct} onChange={e => setQuorumPct(e.target.value)} className="w-1/2" />
            </div>
          </div>
          
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700/50">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Current Attendance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">Members Present (In-Person)</label>
                <Input type="number" value={presentMembers} onChange={e => setPresentMembers(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">Valid Proxies Held</label>
                <Input type="number" value={proxies} onChange={e => setProxies(e.target.value)} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-4 flex flex-col">
        <Card className={cn(
          "p-6 flex flex-col justify-center items-center text-center",
          isQuorumMet ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'
        )}>
          <span className={cn("text-sm font-bold uppercase mb-2 tracking-widest", isQuorumMet ? 'text-green-500' : 'text-red-500')}>
            {isQuorumMet ? 'Quorum Met' : 'Quorum Not Met'}
          </span>
          <span className={cn("text-5xl font-black font-mono", isQuorumMet ? 'text-green-400' : 'text-red-400')}>
            {totalRepresented} <span className="text-2xl opacity-50">/ {requiredToQuorum}</span>
          </span>
          <p className={cn("text-xs mt-4 max-w-sm", isQuorumMet ? 'text-green-400/80' : 'text-red-400/80')}>
            {isQuorumMet 
              ? 'You have enough members/proxies present to conduct official business and hold binding votes.' 
              : `You need ${requiredToQuorum - totalRepresented} more members to reach the required ${requiredToQuorum} for official business.`}
          </p>
        </Card>

        {isQuorumMet && (
          <Card className="p-6">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest block mb-4">Required Vote Thresholds</span>
            <div className="space-y-3 font-mono text-sm">
              <div className="flex justify-between items-center py-2 border-b border-slate-200 dark:border-slate-700/50">
                <span className="text-slate-700 dark:text-slate-300">Majority (Standard Motions)</span>
                <span className="font-bold text-slate-900 dark:text-white text-lg">{majorityThreshold} <span className="text-xs text-slate-500 font-sans font-normal">votes</span></span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-700 dark:text-slate-300">2/3rds (Bylaws/Rules Changes)</span>
                <span className="font-bold text-slate-900 dark:text-white text-lg">{twoThirdsThreshold} <span className="text-xs text-slate-500 font-sans font-normal">votes</span></span>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-4 leading-relaxed">
              * Thresholds are calculated based on the <strong>{totalRepresented}</strong> members currently represented (present + proxies). Abstentions reduce the voting pool and may alter this number depending on your specific parliamentary rules.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

